import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { analyticsAPI, caseAPI, documentAPI, csatAPI, complaintAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { OfficerCSATCard, CompanyCSATCard } from '../components/CSATComponents';
import { ComplaintsStatsCard } from '../components/ComplaintsStats';
import { SuccessRatioCard } from '../components/SuccessRatioCard';
import DateRangeFilter from '../components/DateRangeFilter';
import { 
  Users, 
  Briefcase, 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign,
  TrendingUp,
  FileText,
  AlertTriangle,
  Bell,
  ChevronRight,
  Star,
  MessageSquare,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [urgentCases, setUrgentCases] = useState([]);
  const [companyCSAT, setCompanyCSAT] = useState(null);
  const [officerCSAT, setOfficerCSAT] = useState(null);
  const [complaintsStats, setComplaintsStats] = useState(null);
  const [successRatio, setSuccessRatio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const navigate = useNavigate();

  const isOfficer = user?.role === 'case_officer';
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'process_manager';

  useEffect(() => {
    fetchData();
  }, [selectedDays, customStartDate, customEndDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch filtered analytics
      try {
        const analyticsRes = await analyticsAPI.getFiltered(selectedDays, customStartDate, customEndDate);
        setAnalytics(analyticsRes.data);
      } catch (err) {
        console.log('Filtered analytics not available, using default');
        const casesRes = await caseAPI.getCases();
        setAnalytics({
          total_clients: 0,
          total_cases: casesRes.data.length,
          active_cases: casesRes.data.filter(c => !['Approved', 'Rejected', 'Closed'].includes(c.stage)).length,
          approved_cases: casesRes.data.filter(c => c.stage === 'Approved').length,
          rejected_cases: casesRes.data.filter(c => c.stage === 'Rejected').length,
          total_revenue: 0,
          cases_by_stage: []
        });
      }

      // Fetch recent cases
      const casesRes = await caseAPI.getCases();
      setRecentCases(casesRes.data.slice(0, 5));

      // Fetch complaints stats
      try {
        const complaintsRes = await complaintAPI.getStats(selectedDays, customStartDate, customEndDate);
        setComplaintsStats(complaintsRes.data);
      } catch (err) {
        console.log('Could not fetch complaints stats');
      }

      // Fetch success ratio
      try {
        const successRes = await analyticsAPI.getSuccessRatio(selectedDays, customStartDate, customEndDate);
        // Map the API response to what the UI expects
        const overall = successRes.data?.overall || {};
        setSuccessRatio({
          success_ratio: overall.success_rate,
          approved: overall.approved,
          rejected: overall.rejected,
          total_completed: overall.total
        });
      } catch (err) {
        console.log('Could not fetch success ratio');
      }
      
      // Fetch company CSAT for admins
      if (isAdmin) {
        try {
          const csatRes = await csatAPI.getCompanyCSAT(selectedDays);
          setCompanyCSAT(csatRes.data);
        } catch (err) {
          console.log('Could not fetch company CSAT');
        }
      }
      
      // Fetch officer's own CSAT
      if (isOfficer) {
        try {
          const myCSATRes = await csatAPI.getMyCSAT(selectedDays);
          setOfficerCSAT(myCSATRes.data);
        } catch (err) {
          console.log('Could not fetch officer CSAT');
        }
      }
      
      // For case officers, fetch pending documents
      if (isOfficer && casesRes.data.length > 0) {
        const allPendingDocs = [];
        const urgent = [];
        
        for (const case_ of casesRes.data) {
          try {
            const docsRes = await documentAPI.getCaseDocuments(case_.id);
            const pending = docsRes.data.filter(d => d.status === 'uploaded');
            pending.forEach(doc => {
              allPendingDocs.push({ ...doc, caseId: case_.id });
            });
            
            const daysSinceCreated = Math.floor((new Date() - new Date(case_.created_at)) / (1000 * 60 * 60 * 24));
            if (daysSinceCreated > 10 && pending.length > 0) {
              urgent.push(case_);
            }
          } catch (err) {
            console.log('Could not fetch documents for case', case_.id);
          }
        }
        
        setPendingDocs(allPendingDocs.slice(0, 10));
        setUrgentCases(urgent);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (days) => {
    setSelectedDays(days);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleCustomDateChange = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  const getCasePath = (caseId) => {
    switch (user?.role) {
      case 'super_admin':
        return `/admin/cases/${caseId}`;
      case 'process_manager':
        return `/manager/cases/${caseId}`;
      case 'case_officer':
        return `/officer/cases/${caseId}`;
      default:
        return `/admin/cases/${caseId}`;
    }
  };

  const getCasesPath = () => {
    switch (user?.role) {
      case 'super_admin':
        return '/admin/cases';
      case 'process_manager':
        return '/manager/cases';
      case 'case_officer':
        return '/officer/cases';
      default:
        return '/admin/cases';
    }
  };

  const getClientsPath = () => {
    switch (user?.role) {
      case 'super_admin':
        return '/admin/clients';
      case 'process_manager':
        return '/manager/clients';
      default:
        return '/admin/clients';
    }
  };

  const stats = [
    // MOST IMPORTANT FOR BUSINESS OWNERS - Revenue & Success
    {
      icon: DollarSign,
      label: 'Total Revenue',
      value: `₹${analytics?.total_revenue?.toLocaleString('en-IN') || 0}`,
      color: 'bg-emerald-600',
      testId: 'stat-revenue',
      link: getClientsPath(),
      showFor: ['super_admin', 'admin']
    },
    {
      icon: Trophy,
      label: isOfficer ? 'My Success Rate' : 'Success Rate',
      value: successRatio?.success_ratio !== null ? `${successRatio?.success_ratio}%` : '--',
      color: successRatio?.success_ratio >= 75 ? 'bg-green-500' : successRatio?.success_ratio >= 50 ? 'bg-amber-500' : 'bg-red-500',
      testId: 'stat-success-ratio',
      link: null,
      showFor: ['super_admin', 'admin', 'process_manager', 'case_officer'],
      subValue: successRatio?.total_completed > 0 ? `${successRatio.approved}/${successRatio.total_completed} approved` : null
    },
    {
      icon: Briefcase,
      label: 'Total Cases',
      value: analytics?.total_cases || 0,
      color: 'bg-blue-500',
      testId: 'stat-total-cases',
      link: getCasesPath(),
      showFor: ['super_admin', 'admin', 'process_manager']
    },
    {
      icon: Briefcase,
      label: isOfficer ? 'My Cases' : 'Active Cases',
      value: analytics?.active_cases || 0,
      color: 'bg-teal-500',
      testId: 'stat-active-cases',
      link: `${getCasesPath()}?filter=active`,
      showFor: ['super_admin', 'admin', 'process_manager', 'case_officer']
    },
    {
      icon: Clock,
      label: 'Suspended',
      value: analytics?.suspended_cases || 0,
      color: analytics?.suspended_cases > 0 ? 'bg-orange-500' : 'bg-slate-400',
      testId: 'stat-suspended',
      link: `${getCasesPath()}?filter=suspended`,
      showFor: ['super_admin', 'admin', 'process_manager'],
      highlight: analytics?.suspended_cases > 0
    },
    {
      icon: AlertTriangle,
      label: 'Expired',
      value: analytics?.expired_cases || 0,
      color: analytics?.expired_cases > 0 ? 'bg-red-400' : 'bg-slate-400',
      testId: 'stat-expired',
      link: `${getCasesPath()}?filter=expired`,
      showFor: ['super_admin', 'admin', 'process_manager']
    },
    {
      icon: Clock,
      label: 'Pending Review',
      value: pendingDocs.length,
      color: 'bg-amber-500',
      testId: 'stat-pending-review',
      link: getCasesPath(),
      showFor: ['case_officer']
    },
    {
      icon: CheckCircle,
      label: 'Completed',
      value: analytics?.completed_cases || 0,
      color: 'bg-green-600',
      testId: 'stat-completed',
      link: `${getCasesPath()}?filter=completed`,
      showFor: ['super_admin', 'admin', 'process_manager']
    },
    {
      icon: CheckCircle,
      label: 'Approved',
      value: analytics?.approved_cases || 0,
      color: 'bg-green-500',
      testId: 'stat-approved',
      link: `${getCasesPath()}?filter=approved`,
      showFor: ['super_admin', 'admin', 'process_manager', 'case_officer']
    },
    {
      icon: XCircle,
      label: 'Rejected',
      value: analytics?.rejected_cases || 0,
      color: analytics?.rejected_cases > 0 ? 'bg-red-500' : 'bg-slate-400',
      testId: 'stat-rejected',
      link: `${getCasesPath()}?filter=rejected`,
      showFor: ['super_admin', 'admin', 'process_manager']
    },
    {
      icon: MessageSquare,
      label: 'Complaints',
      value: complaintsStats?.total_complaints || 0,
      color: complaintsStats?.open_complaints > 0 ? 'bg-orange-500' : 'bg-slate-400',
      testId: 'stat-complaints',
      link: null,
      showFor: ['super_admin', 'admin', 'process_manager', 'case_officer'],
      subValue: complaintsStats?.open_complaints > 0 ? `${complaintsStats.open_complaints} open` : null
    },
    {
      icon: Star,
      label: 'My CSAT Score',
      value: officerCSAT?.average_csat ? `${officerCSAT.average_csat}%` : '--',
      color: officerCSAT?.average_csat > 85 ? 'bg-green-500' : officerCSAT?.average_csat > 75 ? 'bg-amber-500' : 'bg-red-500',
      testId: 'stat-officer-csat',
      link: null,
      showFor: ['case_officer'],
      subValue: officerCSAT?.total_ratings > 0 ? `${officerCSAT.total_ratings} ratings` : null
    },
    {
      icon: Star,
      label: 'Company CSAT',
      value: companyCSAT?.average_csat ? `${companyCSAT.average_csat}%` : '--',
      color: companyCSAT?.average_csat > 85 ? 'bg-green-500' : companyCSAT?.average_csat > 75 ? 'bg-amber-500' : 'bg-red-500',
      testId: 'stat-company-csat',
      link: null,
      showFor: ['super_admin', 'admin', 'process_manager'],
      isCSAT: true
    },
    {
      icon: Users,
      label: 'Total Clients',
      value: analytics?.total_clients || 0,
      color: 'bg-blue-500',
      testId: 'stat-total-clients',
      link: getClientsPath(),
      showFor: ['super_admin', 'admin', 'process_manager']
    }
  ].filter(stat => stat.showFor.includes(user?.role));

  const getStageColor = (stage) => {
    const colors = {
      'New Client': 'status-new',
      'Agreement Sent': 'status-pending',
      'Agreement Signed': 'status-active',
      'Documents Pending': 'status-pending',
      'Approved': 'status-approved',
      'Rejected': 'status-rejected'
    };
    return colors[stage] || 'status-pending';
  };

  if (loading && !analytics) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content bg-slate-50" data-testid="admin-dashboard">
        {/* Header with Profile Menu */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-slate-500 text-sm">Company operations overview</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter
              selectedDays={selectedDays}
              onDaysChange={setSelectedDays}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onCustomRangeChange={(start, end) => {
                setCustomStartDate(start);
                setCustomEndDate(end);
              }}
            />
            <div className="w-px h-8 bg-slate-200"></div>

          </div>
        </div>

        {/* Stats Grid - Compact 5-column layout */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-5" data-testid="stats-grid">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.testId}
                className={`bg-white rounded-xl border p-3.5 transition-all duration-200 ${
                  stat.link ? 'cursor-pointer hover:shadow-md hover:border-teal-300 hover:-translate-y-0.5' : ''
                } ${stat.highlight ? 'border-orange-300 bg-orange-50/50' : 'border-slate-200'}`}
                data-testid={stat.testId}
                onClick={() => stat.link && navigate(stat.link)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                    <Icon className="text-teal-600" size={16} />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-500 mb-0.5">{stat.label}</p>
                <p className="text-xl font-bold text-slate-900">{stat.value}</p>
                {stat.subValue && (
                  <p className={`text-xs mt-0.5 ${stat.highlight ? 'text-orange-600 font-medium' : 'text-slate-400'}`}>
                    {stat.subValue}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Case Officer Action Items - Shown prominently at top */}
        {isOfficer && (pendingDocs.length > 0 || urgentCases.length > 0) && (
          <div className="mb-6 space-y-4">
            {/* Urgent Cases Alert */}
            {urgentCases.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="urgent-cases-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="text-red-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-red-800">{urgentCases.length} Urgent Case{urgentCases.length > 1 ? 's' : ''}</h3>
                      <p className="text-sm text-red-600">Documents pending for over 10 days</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(getCasesPath())}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-all flex items-center gap-1"
                  >
                    View Cases
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Pending Documents Summary */}
            {pendingDocs.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pending-docs-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <FileText className="text-amber-600" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-800">{pendingDocs.length} Document{pendingDocs.length > 1 ? 's' : ''} Pending Review</h3>
                      <p className="text-sm text-amber-600">
                        {pendingDocs.slice(0, 3).map(d => d.checklist_item_name).join(', ')}
                        {pendingDocs.length > 3 && ` +${pendingDocs.length - 3} more`}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate('/officer/documents')}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-all flex items-center gap-1"
                  >
                    Review Documents
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="recent-cases-card">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileText size={24} className="text-primary" />
              {isOfficer ? 'My Cases' : 'Recent Cases'}
            </h2>
            <div className="space-y-3">
              {recentCases.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No cases yet</p>
              ) : (
                recentCases.map((case_) => (
                  <div 
                    key={case_.id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all cursor-pointer" 
                    data-testid={`case-item-${case_.id}`}
                    onClick={() => navigate(getCasePath(case_.id))}
                  >
                    <div>
                      <p className="font-medium text-slate-900">Case #{case_.id.slice(0, 8)}</p>
                      <p className="text-sm text-slate-500">{new Date(case_.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`status-badge ${getStageColor(case_.stage)}`}>
                      {case_.stage}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="cases-by-stage-card">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp size={24} className="text-primary" />
              Cases by Stage
            </h2>
            <div className="space-y-3">
              {analytics?.cases_by_stage?.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No data available</p>
              ) : (
                analytics?.cases_by_stage?.map((stage) => (
                  <div key={stage._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium text-slate-700">{stage._id}</span>
                    <span className="text-2xl font-bold text-primary">{stage.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Complaints Stats Card */}
          <ComplaintsStatsCard 
            isOfficer={isOfficer} 
            onOfficerClick={(officerId) => console.log('View officer complaints:', officerId)}
          />

          {/* Success Ratio Card */}
          <SuccessRatioCard 
            isOfficer={isOfficer}
            onOfficerClick={(officerId) => console.log('View officer success:', officerId)}
          />

          {/* CSAT Card for Case Officers */}
          {isOfficer && (
            <OfficerCSATCard showLegend={true} />
          )}

          {/* CSAT Card for Admins */}
          {isAdmin && (
            <CompanyCSATCard onOfficerClick={(officerId) => console.log('View officer:', officerId)} />
          )}
        </div>
      </div>
    </div>
  );
}
